import {
  CONJOINT_NONE_LABEL,
  formatConjointProfileParts,
} from "@/components/interview/conjoint/answerFormat";
import {
  getAnsweredConjointTasks,
  getConjointTradeoff,
  getFavoredConjointLevel,
} from "@/components/interview/conjoint/followUpSignals";
import {
  getCurrencyCode,
  getPriceLadder,
} from "@/components/interview/gaborGranger";
import {
  formatPrice,
  formatPriceRange,
} from "@/components/interview/gaborGranger/answerFormat";
import { getEstablishedSwitchPointIndex } from "@/components/interview/gaborGranger/ladder";
import type {
  ConjointProfile,
  GaborGrangerAnswer,
  GaborGrangerRung,
  InterviewItem,
  OptionId,
  QAState,
  QuestionType,
} from "@/components/interview/types";
import { notNull } from "@/utils/helpers";

export type AnswerReferenceResolveArgs = {
  statesForItem: QAState[];
  item: InterviewItem;
};

export type AnswerReferenceAccessor =
  | "md-mostPickedBest"
  | "md-mostPickedWorst"
  | "rank-topRanked"
  | "rank-bottomRanked"
  | "gg-switchPrice"
  | "cj-mostChosenLevel"
  | "cj-tradeoffChosen"
  | "cj-tradeoffPassedOver";

/**
 * One selectable way to reuse a prior answer in later question text.
 *
 * The editor uses this to decide which pills to show, validation uses it to
 * check whether a token is legal for the referenced question type, and runtime
 * substitution uses `resolve` to compute the value from the respondent's
 * structured answer history.
 */
export type AnswerReferenceDefinition = {
  /** Source question type this reference can read from. */
  questionType: QuestionType;
  /** Token suffix. `undefined` means the bare `{{answer:questionId}}` form. */
  accessor: AnswerReferenceAccessor | undefined;
  /** Human label shown in autocomplete and pills, after the question number. */
  label: string;
  /** Phrase used when the same pill is read aloud by TTS. */
  spoken: (questionNumber: number) => string;
  /** Resolves the referenced value from QAState history and the source item. */
  resolve: (args: AnswerReferenceResolveArgs) => string | string[] | undefined;
  /**
   * The options behind this reference, so a later question can show their
   * pictures. Absent when the reference resolves to something else.
   */
  getOptionIds?: (args: AnswerReferenceResolveArgs) => OptionId[] | undefined;
};

// Option ids and their text come apart because a reference can be rendered as
// the label in question text or as the option's own picture.
const getOptionTexts = (
  item: InterviewItem,
  ids: OptionId[] | undefined
): string[] | undefined => {
  if (!ids) return undefined;
  const texts = ids
    .map((id) => item.choices?.find((c) => c.id === id)?.text)
    .filter(notNull);
  return texts.length > 0 ? texts : undefined;
};

const pickMultipleChoiceOptionIds = ({
  statesForItem,
  item,
}: AnswerReferenceResolveArgs): OptionId[] | undefined => {
  if (!item.choices) return undefined;
  const lastWithSelection = [...statesForItem]
    .reverse()
    .find((s) => s.selectedOptionIds && s.selectedOptionIds.length > 0);
  return lastWithSelection?.selectedOptionIds;
};

const resolveMultipleChoiceAnswer = (
  args: AnswerReferenceResolveArgs
): string[] | undefined =>
  getOptionTexts(args.item, pickMultipleChoiceOptionIds(args));

const pickMostFrequentMaxDiffOptionId = (
  field: "bestPick" | "worstPick",
  { statesForItem, item }: AnswerReferenceResolveArgs
): OptionId | undefined => {
  if (!item.choices) return undefined;
  const picks = statesForItem
    .map((s) => s.maxDiffScreen?.[field])
    .filter(notNull);
  if (picks.length === 0) return undefined;

  const counts = new Map<OptionId, { count: number; firstIndex: number }>();
  picks.forEach((id, index) => {
    const existing = counts.get(id);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(id, { count: 1, firstIndex: index });
    }
  });

  let winner: OptionId | undefined;
  let winnerEntry: { count: number; firstIndex: number } | undefined;
  for (const [id, entry] of counts) {
    if (
      !winnerEntry ||
      entry.count > winnerEntry.count ||
      (entry.count === winnerEntry.count &&
        entry.firstIndex < winnerEntry.firstIndex)
    ) {
      winner = id;
      winnerEntry = entry;
    }
  }
  return winner;
};

const resolveMostFrequentMaxDiffPick = (
  field: "bestPick" | "worstPick",
  args: AnswerReferenceResolveArgs
): string | undefined =>
  getOptionTexts(
    args.item,
    [pickMostFrequentMaxDiffOptionId(field, args)].filter(notNull)
  )?.[0];

const pickRankingEndpointOptionId = (
  position: "top" | "bottom",
  { statesForItem, item }: AnswerReferenceResolveArgs
): OptionId | undefined => {
  if (!item.choices) return undefined;
  const lastWithSelection = [...statesForItem]
    .reverse()
    .find((s) => s.selectedOptionIds && s.selectedOptionIds.length > 0);
  if (!lastWithSelection) return undefined;

  const { selectedOptionIds } = lastWithSelection;
  const targetId =
    position === "top"
      ? selectedOptionIds[0]
      : selectedOptionIds[selectedOptionIds.length - 1];
  return targetId;
};

const resolveRankingEndpoint = (
  position: "top" | "bottom",
  args: AnswerReferenceResolveArgs
): string | undefined =>
  getOptionTexts(
    args.item,
    [pickRankingEndpointOptionId(position, args)].filter(notNull)
  )?.[0];

// One item's submitted rungs, deduped and ordered exactly as extraction does, so
// a follow-up can never describe a walk the demand curve read differently.
const answeredRungsInOrder = ({
  statesForItem,
}: AnswerReferenceResolveArgs): GaborGrangerAnswer[] => {
  const latestByRungIndex = new Map<
    number,
    GaborGrangerRung & GaborGrangerAnswer
  >();
  for (const state of statesForItem) {
    const rung = state.gaborGrangerRung;
    // A corrected rung is appended rather than swapped in, so last one wins.
    if (
      rung &&
      state.submittedAt !== undefined &&
      rung.accepted !== undefined
    ) {
      latestByRungIndex.set(rung.rungIndex, {
        ...rung,
        accepted: rung.accepted,
      });
    }
  }
  return [...latestByRungIndex.values()].sort(
    (a, b) => a.rungIndex - b.rungIndex
  );
};

// The price they would buy at, or the ladder's span when the walk never pinned
// one down, so every real walk resolves rather than rendering a raw token.
const getGaborGrangerSwitchPrice = (
  args: AnswerReferenceResolveArgs
): string | undefined => {
  const priceLadder = getPriceLadder(args.item);
  // No ladder authored yet, so no price to name, as the siblings do for choices.
  if (priceLadder.length === 0) return undefined;
  const currencyCode = getCurrencyCode(args.item);
  // The turn's language, as the screen does, so the amount is not formatted with
  // the server's host locale for every respondent alike.
  const locale = args.statesForItem.at(-1)?.language;
  const switchPointIndex = getEstablishedSwitchPointIndex({
    answers: answeredRungsInOrder(args),
    priceCount: priceLadder.length,
  });
  // `-1` is a real answer (declined every price) but names no price to quote.
  if (switchPointIndex === undefined || switchPointIndex < 0)
    return formatPriceRange(
      priceLadder[0],
      priceLadder[priceLadder.length - 1],
      currencyCode,
      locale
    );

  return formatPrice(priceLadder[switchPointIndex], currencyCode, locale);
};

// The whole profile, so the same text works for a trade-off and for asking
// whether they would really buy it.
const describeConjointProfile = (
  profile: ConjointProfile,
  item: InterviewItem
): string =>
  formatConjointProfileParts(profile, item.attributes ?? []).join(", ");

// Both sides of one trade-off come from the same pure selection, so a question
// naming both always describes a single real choice.
const formatConjointTradeoffSide = (
  side: "chosen" | "passedOver",
  { statesForItem, item }: AnswerReferenceResolveArgs
): string | undefined => {
  const tradeoff = getConjointTradeoff(
    item,
    getAnsweredConjointTasks(statesForItem)
  );
  if (!tradeoff) return undefined;
  const profile = tradeoff[side];
  return profile === null
    ? CONJOINT_NONE_LABEL
    : describeConjointProfile(profile, item);
};

/**
 * The `accessor` doubles as the token suffix persisted in question text
 * (`{{answer:id:accessor}}`, or the bare `{{answer:id}}` for `undefined`) and as
 * the lookup key in `getAnswerReferenceByAccessor`. So every accessor must be
 * unique across the registry, otherwise the `.find` lookup resolves the wrong
 * entry and later definitions become unreachable. To keep uniqueness obvious and
 * collision-proof, namespace new accessors by question type (e.g. `md-` for
 * max_diff). The one `undefined` accessor is the single legacy bare-reference
 * form. Uniqueness is guarded by a test (see answerReferenceRegistry.test.ts).
 */
export const answerReferenceRegistry = [
  // Bare token: {{answer:questionId}}. The `undefined` accessor is legacy: it
  // was the original single-reference form and is fine to keep. Going forward,
  // every new entry should declare a unique, non-undefined accessor namespaced by
  // its question type (see the `md-` max_diff accessors below).
  {
    questionType: "multiple_choice",
    accessor: undefined,
    label: "answer",
    spoken: (q) => `your answer to question ${q}`,
    resolve: resolveMultipleChoiceAnswer,
    getOptionIds: pickMultipleChoiceOptionIds,
  },
  // Named token: {{answer:questionId:md-mostPickedBest}}
  {
    questionType: "max_diff",
    accessor: "md-mostPickedBest",
    label: "most often selected as most",
    spoken: (q) => `the option most often selected as most in question ${q}`,
    resolve: (args) => resolveMostFrequentMaxDiffPick("bestPick", args),
    getOptionIds: (args) =>
      [pickMostFrequentMaxDiffOptionId("bestPick", args)].filter(notNull),
  },
  // Named token: {{answer:questionId:md-mostPickedWorst}}
  {
    questionType: "max_diff",
    accessor: "md-mostPickedWorst",
    label: "most often selected as least",
    spoken: (q) => `the option most often selected as least in question ${q}`,
    resolve: (args) => resolveMostFrequentMaxDiffPick("worstPick", args),
    getOptionIds: (args) =>
      [pickMostFrequentMaxDiffOptionId("worstPick", args)].filter(notNull),
  },
  // Named token: {{answer:questionId:rank-topRanked}}
  {
    questionType: "ranking",
    accessor: "rank-topRanked",
    label: "top ranked",
    spoken: (q) => `your top ranked option in question ${q}`,
    resolve: (args) => resolveRankingEndpoint("top", args),
    getOptionIds: (args) =>
      [pickRankingEndpointOptionId("top", args)].filter(notNull),
  },
  // Named token: {{answer:questionId:rank-bottomRanked}}
  {
    questionType: "ranking",
    accessor: "rank-bottomRanked",
    label: "bottom ranked",
    spoken: (q) => `your bottom ranked option in question ${q}`,
    resolve: (args) => resolveRankingEndpoint("bottom", args),
    getOptionIds: (args) =>
      [pickRankingEndpointOptionId("bottom", args)].filter(notNull),
  },
  // Named token: {{answer:questionId:gg-switchPrice}}
  {
    questionType: "gabor_granger",
    accessor: "gg-switchPrice",
    label: "the price they would buy at",
    spoken: (q) => `the price you would buy at in question ${q}`,
    resolve: getGaborGrangerSwitchPrice,
  },
  // Named token: {{answer:questionId:cj-mostChosenLevel}}
  {
    questionType: "conjoint",
    accessor: "cj-mostChosenLevel",
    label: "the level they tended to pick",
    spoken: (q) => `what you tended to pick in question ${q}`,
    resolve: ({ statesForItem, item }) =>
      getFavoredConjointLevel(item, getAnsweredConjointTasks(statesForItem)),
  },
  // Named token: {{answer:questionId:cj-tradeoffChosen}}
  {
    questionType: "conjoint",
    accessor: "cj-tradeoffChosen",
    label: "an option they chose over another",
    spoken: (q) => `an option you chose in question ${q}`,
    resolve: (args) => formatConjointTradeoffSide("chosen", args),
  },
  // Named token: {{answer:questionId:cj-tradeoffPassedOver}}
  {
    questionType: "conjoint",
    accessor: "cj-tradeoffPassedOver",
    label: "the option they passed over for it",
    spoken: (q) => `an option you passed over in question ${q}`,
    resolve: (args) => formatConjointTradeoffSide("passedOver", args),
  },
] as const satisfies readonly AnswerReferenceDefinition[];

export const getAnswerReferenceByAccessor = (
  accessor: AnswerReferenceAccessor | undefined
): AnswerReferenceDefinition | undefined =>
  answerReferenceRegistry.find((def) => def.accessor === accessor);

export const isAnswerReferenceAccessor = (
  value: string
): value is AnswerReferenceAccessor =>
  answerReferenceRegistry.some((def) => def.accessor === value);

export const parseAnswerReferenceAccessor = (
  value: string | undefined
): AnswerReferenceAccessor | undefined =>
  value !== undefined && isAnswerReferenceAccessor(value) ? value : undefined;

export const getAnswerReferencesForQuestionType = (
  questionType: QuestionType
): AnswerReferenceDefinition[] =>
  answerReferenceRegistry.filter((def) => def.questionType === questionType);

/**
 * Generates prompt-friendly documentation of all answer reference accessors,
 * grouped by question type. Used by the creation AI prompt so new accessors
 * automatically appear without a manual prompt update.
 */
export const generateAnswerReferenceDocs = (): string => {
  const grouped = new Map<string, AnswerReferenceDefinition[]>();
  for (const def of answerReferenceRegistry) {
    const list = grouped.get(def.questionType) ?? [];
    list.push(def);
    grouped.set(def.questionType, list);
  }
  return [...grouped.entries()]
    .map(([type, defs]) => {
      const tokens = defs
        .map((d) =>
          d.accessor
            ? `\`{{answer:Q#:${d.accessor}}}\` (${d.label})`
            : `\`{{answer:Q#}}\` (${d.label})`
        )
        .join(", ");
      return `  * **${type.replace(/_/g, " ")}:** ${tokens}`;
    })
    .join("\n");
};
